<!-- Backdrop for blocking notifications -->
@if (hasBlockingNotifications()) {
  <div class="fixed inset-0 bg-black/50 z-[55]" aria-hidden="true"></div>
}

<!-- Non-blocking notifications (regular toasts) -->
@if (nonBlockingNotifications().length > 0) {
  <div [class]="getPositionClasses()">
    @for (notification of nonBlockingNotifications(); track notification.id) {
      <div
        [@slideIn]
        [@fadeOut]
        [id]="'notification-' + notification.id"
        class="bg-white dark:bg-surface-900 text-gray-900 dark:text-surface-0 rounded-lg shadow-lg overflow-hidden border border-gray-200 dark:border-surface-700"
        role="alert"
        [attr.aria-live]="notification.severity === 'error' ? 'assertive' : 'polite'"
        [attr.aria-atomic]="true"
      >
        <div class="p-4">
          <!-- Header -->
          <div class="flex items-start justify-between gap-3">
            <div class="flex items-center gap-3 flex-1">
              <i
                [class]="
                  getIcon(notification.severity, notification.icon) +
                  ' ' +
                  getIconColor(notification.severity) +
                  ' text-2xl'
                "
                aria-hidden="true"
              ></i>
              <div class="flex-1">
                <h3 class="font-semibold text-base" [id]="'title-' + notification.id">
                  {{ notification.title }}
                </h3>
              </div>
            </div>
            <div class="flex items-center gap-2">
              @if (notification.detail) {
                <button
                  (click)="toggleExpand(notification.id)"
                  class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-transform"
                  [attr.aria-label]="
                    isExpanded(notification.id)
                      ? 'Collapse notification details'
                      : 'Expand notification details'
                  "
                  [attr.aria-expanded]="isExpanded(notification.id)"
                  [attr.aria-controls]="'expandable-' + notification.id"
                >
                  <i
                    [class]="
                      'pi ' + (isExpanded(notification.id) ? 'pi-chevron-up' : 'pi-chevron-down')
                    "
                    aria-hidden="true"
                  ></i>
                </button>
              }
              @if (notification.dismissible !== false) {
                <button
                  (click)="dismiss(notification.id)"
                  class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:rotate-45 transition-transform"
                  aria-label="Close notification"
                >
                  <i class="pi pi-times" aria-hidden="true"></i>
                </button>
              }
            </div>
          </div>

          <!-- Summary -->
          <div class="mt-3">
            <p class="text-sm">{{ notification.summary }}</p>
          </div>

          <!-- Expandable Detail -->
          @if (notification.detail) {
            <div
              [id]="'expandable-' + notification.id"
              [class]="isExpanded(notification.id) ? 'expanded-content' : 'collapsed-content'"
              class="transition-height"
              [attr.aria-labelledby]="'title-' + notification.id"
            >
              <p class="text-sm mt-3 text-gray-700 dark:text-gray-300">
                {{ notification.detail }}
              </p>

              @if (notification.actions && notification.actions.length > 0) {
                <div class="mt-4 flex gap-2">
                  @for (action of notification.actions; track action.label) {
                    <button
                      (click)="handleAction(action, notification.id)"
                      class="px-4 py-2 bg-white dark:bg-surface-800 border-2 {{
                        getBorderColor(notification.severity)
                      }} rounded-lg hover:bg-gray-50 dark:hover:bg-surface-700 transition text-sm font-medium"
                    >
                      {{ action.label }}
                    </button>
                  }
                </div>
              } @else {
                <div class="mt-4">
                  <button
                    (click)="dismiss(notification.id)"
                    class="px-6 py-2 bg-white dark:bg-surface-800 border border-gray-300 dark:border-surface-600 rounded-lg hover:bg-gray-50 dark:hover:bg-surface-700 transition text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Okay
                  </button>
                </div>
              }
            </div>
          }

          <!-- Timer Text -->
          @if (notification.duration && notification.duration > 0) {
            <div class="mt-3 text-xs text-gray-500 dark:text-gray-400" aria-live="polite">
              <span>
                This message will close in {{ getRemainingSeconds(notification.id) }} seconds.
              </span>
              <button
                (click)="stopTimer(notification.id)"
                class="underline hover:no-underline ml-1"
                aria-label="Stop auto-close timer"
              >
                Click to stop</button
              >.
            </div>
          }
        </div>

        <!-- Progress Bar -->
        @if (notification.duration && notification.duration > 0) {
          <div
            class="h-1 bg-gray-200 dark:bg-surface-700"
            role="progressbar"
            [attr.aria-valuenow]="getProgressPercentage(notification)"
            [attr.aria-valuemin]="0"
            [attr.aria-valuemax]="100"
          >
            <div
              class="h-full {{
                getProgressColor(notification.severity)
              }} transition-all duration-100 ease-linear"
              [style.width.%]="getProgressPercentage(notification)"
            ></div>
          </div>
        }
      </div>
    }
  </div>
}

<!-- Blocking notifications (modal-like) -->
@if (blockingNotifications().length > 0) {
  <div
    class="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] flex flex-col gap-3 w-full max-w-md px-4"
  >
    @for (notification of blockingNotifications(); track notification.id) {
      <div
        [@slideIn]
        [@fadeOut]
        [id]="'notification-' + notification.id"
        class="bg-white dark:bg-surface-900 text-gray-900 dark:text-surface-0 rounded-lg shadow-lg overflow-hidden border border-gray-200 dark:border-surface-700"
        role="alert"
        [attr.aria-live]="notification.severity === 'error' ? 'assertive' : 'polite'"
        [attr.aria-atomic]="true"
      >
        <div class="p-4">
          <!-- Header -->
          <div class="flex items-start justify-between gap-3">
            <div class="flex items-center gap-3 flex-1">
              <i
                [class]="
                  getIcon(notification.severity, notification.icon) +
                  ' ' +
                  getIconColor(notification.severity) +
                  ' text-2xl'
                "
                aria-hidden="true"
              ></i>
              <div class="flex-1">
                <h3 class="font-semibold text-base" [id]="'title-' + notification.id">
                  {{ notification.title }}
                </h3>
              </div>
            </div>
            <div class="flex items-center gap-2">
              @if (notification.detail) {
                <button
                  (click)="toggleExpand(notification.id)"
                  class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-transform"
                  [attr.aria-label]="
                    isExpanded(notification.id)
                      ? 'Collapse notification details'
                      : 'Expand notification details'
                  "
                  [attr.aria-expanded]="isExpanded(notification.id)"
                  [attr.aria-controls]="'expandable-' + notification.id"
                >
                  <i
                    [class]="
                      'pi ' + (isExpanded(notification.id) ? 'pi-chevron-up' : 'pi-chevron-down')
                    "
                    aria-hidden="true"
                  ></i>
                </button>
              }
              @if (notification.dismissible !== false) {
                <button
                  (click)="dismiss(notification.id)"
                  class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:rotate-45 transition-transform"
                  aria-label="Close notification"
                >
                  <i class="pi pi-times" aria-hidden="true"></i>
                </button>
              }
            </div>
          </div>

          <!-- Summary -->
          <div class="mt-3">
            <p class="text-sm">{{ notification.summary }}</p>
          </div>

          <!-- Expandable Detail -->
          @if (notification.detail) {
            <div
              [id]="'expandable-' + notification.id"
              [class]="isExpanded(notification.id) ? 'expanded-content' : 'collapsed-content'"
              class="transition-height"
              [attr.aria-labelledby]="'title-' + notification.id"
            >
              <p class="text-sm mt-3 text-gray-700 dark:text-gray-300">
                {{ notification.detail }}
              </p>

              @if (notification.actions && notification.actions.length > 0) {
                <div class="mt-4 flex gap-2">
                  @for (action of notification.actions; track action.label) {
                    <button
                      (click)="handleAction(action, notification.id)"
                      class="px-4 py-2 bg-white dark:bg-surface-800 border-2 {{
                        getBorderColor(notification.severity)
                      }} rounded-lg hover:bg-gray-50 dark:hover:bg-surface-700 transition text-sm font-medium"
                    >
                      {{ action.label }}
                    </button>
                  }
                </div>
              } @else {
                <div class="mt-4">
                  <button
                    (click)="dismiss(notification.id)"
                    class="px-6 py-2 bg-white dark:bg-surface-800 border border-gray-300 dark:border-surface-600 rounded-lg hover:bg-gray-50 dark:hover:bg-surface-700 transition text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Okay
                  </button>
                </div>
              }
            </div>
          }

          <!-- Timer Text -->
          @if (notification.duration && notification.duration > 0) {
            <div class="mt-3 text-xs text-gray-500 dark:text-gray-400" aria-live="polite">
              <span>
                This message will close in {{ getRemainingSeconds(notification.id) }} seconds.
              </span>
              <button
                (click)="stopTimer(notification.id)"
                class="underline hover:no-underline ml-1"
                aria-label="Stop auto-close timer"
              >
                Click to stop</button
              >.
            </div>
          }
        </div>

        <!-- Progress Bar -->
        @if (notification.duration && notification.duration > 0) {
          <div
            class="h-1 bg-gray-200 dark:bg-surface-700"
            role="progressbar"
            [attr.aria-valuenow]="getProgressPercentage(notification)"
            [attr.aria-valuemin]="0"
            [attr.aria-valuemax]="100"
          >
            <div
              class="h-full {{
                getProgressColor(notification.severity)
              }} transition-all duration-100 ease-linear"
              [style.width.%]="getProgressPercentage(notification)"
            ></div>
          </div>
        }
      </div>
    }
  </div>
}
